import { useEffect, useId, useMemo, useRef, useState } from 'react';
import * as d3 from 'd3';
import { Box, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { CursorTooltip } from './CursorTooltip';
import { LIVE_TAIL_DASH, dashOf, faintOpacityOf } from './dash';
import { createChartGestures, toGesturePointer } from './gestures';
import { formatTick } from './format';
import { fittingTimeLabels, PANEL_MARGIN, type PanelMargins } from './margins';
import {
    axisValues,
    readout,
    seriesView,
    stepMs,
    type SeriesView,
} from './panelData';
import {
    PanelHeader,
    PANEL_HEADER_HEIGHT,
    panelHeaderHeight,
} from './PanelHeader';
import { axisDomain } from './scales';
import {
    bandAt,
    MIN_LABEL_PIXELS,
    MIN_LABEL_SHARE,
    stackLayers,
    type StackBand,
} from './stacked';
import { StackLabels, type StackLabel } from './StackLabels';
import type {
    ChartSeries,
    PanelAxis,
    PanelPlan,
    SeriesMarker,
    SeriesPoint,
} from './seriesTypes';
import { useCompactChart } from './useCompactChart';
import { useViewport } from './viewport';

export { PANEL_HEADER_HEIGHT };
export { PANEL_MARGIN };
export const TIME_AXIS_HEIGHT = 22;

const VOLUME_BAND = 0.2;
const WHEEL_FACTOR = 1.15;
const MARKER_SIZE = 6;

interface PanelProps {
    plan: PanelPlan;
    width: number;
    height: number;
    showTimeAxis: boolean;
    /** The same for every panel of a stack, so the plots line up. */
    margins: PanelMargins;
    /**
     * Width from the right edge that the time axis may not use (the lifetime
     * text lies over it). Only below 900 px, where the margin no longer holds it.
     */
    timeAxisReserve?: number;
}

type YScale = d3.ScaleLinear<number, number>;

function markerPath(shape: SeriesMarker['shape'], s: number): string {
    switch (shape) {
        case 'triangle':
        case 'up':
            return `M0,${-s} L${s},${s} L${-s},${s} Z`;
        case 'down':
            return `M0,${s} L${s},${-s} L${-s},${-s} Z`;
        case 'diamond':
            return `M0,${-s} L${s},0 L0,${s} L${-s},0 Z`;
        case 'circle':
            return `M${-s},0 a${s},${s} 0 1,0 ${2 * s},0 a${s},${s} 0 1,0 ${-2 * s},0`;
    }
}

const LIVE_HALO_STYLE = {
    animation: 'rtHalo 1.8s ease-out infinite',
    transformBox: 'fill-box',
    transformOrigin: 'center',
} as const;

/**
 * The streamed last point of a line, when it is drawn and a candle comes
 * before it. Only lines and steps end in a live tail and dot: a stacked band
 * follows the live share itself, and its point sits on the band top, not at
 * the raw value.
 */
function liveTailOf(
    series: ChartSeries,
    drawn: readonly SeriesPoint[]
): SeriesPoint | null {
    if (series.draw !== 'line' && series.draw !== 'step') return null;
    if (!series.live || series.data.kind !== 'points') return null;
    const live = series.data.points[series.data.points.length - 1];
    const last = drawn[drawn.length - 1];
    return live && last && drawn.length >= 2 && last.t === live.t ? live : null;
}

function markerSize(marker: SeriesMarker): number {
    if (marker.weight === undefined) return MARKER_SIZE;
    return 3.5 + 5 * Math.sqrt(Math.min(1, Math.max(0, marker.weight)));
}

export function Panel({
    plan,
    width,
    height,
    showTimeAxis,
    margins,
    timeAxisReserve = 0,
}: PanelProps) {
    const theme = useTheme();
    const viewport = useViewport();
    const compact = useCompactChart();
    const clipId = useId().replace(/:/g, '');
    const overlayRef = useRef<SVGRectElement>(null);
    const [hovered, setHovered] = useState<{
        marker: SeriesMarker;
        x: number;
        y: number;
    } | null>(null);

    const t0 = viewport.domain[0].getTime();
    const t1 = viewport.domain[1].getTime();
    const headerHeight = panelHeaderHeight(plan);
    const plotHeight =
        height - headerHeight - (showTimeAxis ? TIME_AXIS_HEIGHT : 0);
    const innerW = Math.max(0, width - margins.left - margins.right);
    const innerH = Math.max(0, plotHeight - margins.top - margins.bottom);

    const x = useMemo(
        () =>
            d3
                .scaleTime()
                .domain([new Date(t0), new Date(t1)])
                .range([0, innerW]),
        [t0, t1, innerW]
    );

    const views = useMemo(() => {
        const now = Date.now();
        const map = new Map<string, SeriesView>();
        for (const s of plan.series)
            map.set(s.id, seriesView(s, [t0, t1], now));
        return map;
    }, [plan.series, t0, t1]);

    const hasBars = plan.series.some(
        s =>
            s.draw === 'bars' &&
            (views.get(s.id)?.points ?? []).some(p => p.v > 0)
    );
    const lineBottom = hasBars ? innerH * (1 - VOLUME_BAND) : innerH;

    const stackedIds = useMemo(
        () => plan.series.filter(s => s.draw === 'area').map(s => s.id),
        [plan.series]
    );
    const layers = useMemo(
        () =>
            stackLayers(
                stackedIds.map(id => ({
                    id,
                    points: views.get(id)?.points ?? [],
                }))
            ),
        [stackedIds, views]
    );

    const axisScales = useMemo(() => {
        const map = new Map<string, YScale>();
        for (const axis of plan.axes) {
            const values = axis.seriesIds.flatMap(id => {
                const view = views.get(id);
                return view ? axisValues(view) : [];
            });
            map.set(
                axis.key,
                d3
                    .scaleLinear()
                    .domain(
                        stackedIds.some(id => axis.seriesIds.includes(id))
                            ? [0, 1]
                            : axisDomain(axis.unit, values)
                    )
                    .range([lineBottom, 0])
            );
        }
        return map;
    }, [plan.axes, views, lineBottom, stackedIds]);

    const axisOf = (series: ChartSeries): PanelAxis | undefined =>
        plan.axes.find(a => a.seriesIds.includes(series.id)) ??
        plan.axes.find(a => a.unit === series.unit);

    const scaleOf = (series: ChartSeries): YScale | undefined => {
        const axis = axisOf(series);
        return axis ? axisScales.get(axis.key) : undefined;
    };

    const zoomAt = viewport.zoomAt;
    useEffect(() => {
        const el = overlayRef.current;
        if (!el) return;
        const onWheel = (event: WheelEvent) => {
            event.preventDefault();
            const rect = el.getBoundingClientRect();
            const t = x.invert(event.clientX - rect.left).getTime();
            zoomAt(t, event.deltaY > 0 ? WHEEL_FACTOR : 1 / WHEEL_FACTOR);
        };
        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, [x, zoomAt]);

    const timeAt = (clientX: number): number => {
        const rect = overlayRef.current?.getBoundingClientRect();
        return x.invert(clientX - (rect?.left ?? 0)).getTime();
    };

    const msPerPx = innerW > 0 ? (t1 - t0) / innerW : 0;
    // The gestures read the numbers of the latest render through this ref, so
    // one mapper lives as long as the panel.
    const gestureEnv = {
        timeAt,
        msPerPx,
        // A finger that slides off the plot (onto the axis labels, past the
        // card) keeps reading the edge of the shown range: the pointer is
        // captured, and a time outside the range has no values to show.
        setCursor: (t: number | null) =>
            t === null
                ? viewport.setCursor(null)
                : viewport.setCursor(Math.min(t1, Math.max(t0, t)), plan.id),
        panBy: viewport.panBy,
        zoomAt: viewport.zoomAt,
        reset: viewport.reset,
    };
    const gestureEnvRef = useRef(gestureEnv);
    gestureEnvRef.current = gestureEnv;
    const gestures = useMemo(
        () => createChartGestures(() => gestureEnvRef.current),
        []
    );
    const gridColor = alpha(theme.palette.text.primary, 0.08);
    const axisColor = theme.palette.text.secondary;
    const upColor = theme.palette.success.main;
    const downColor = theme.palette.error.main;
    const firstAxis = plan.axes[0];
    const firstScale = firstAxis ? axisScales.get(firstAxis.key) : undefined;
    const tickCount = innerH < 110 ? 3 : 5;
    const cursor = viewport.cursor;
    const cursorX =
        cursor !== null && cursor >= t0 && cursor <= t1 ? x(cursor) : null;
    const readoutTime = cursor ?? t1;
    const isCursorPanel = viewport.cursorPanel === plan.id;

    // Below 900 px the right margin is a few pixels: a label on the plot's end
    // would be cut off by the svg, and the lifetime text lies over the right
    // end of the axis. Those labels are left out.
    const timeLabels = (() => {
        if (!showTimeAxis) return [];
        const format = x.tickFormat();
        const all = x
            .ticks(Math.max(2, Math.floor(innerW / 120)))
            .map(tick => ({
                t: tick.getTime(),
                x: x(tick),
                text: format(tick),
            }));
        return compact
            ? fittingTimeLabels(
                  all,
                  -margins.left,
                  innerW + margins.right - timeAxisReserve
              )
            : all;
    })();

    const stackLabels: StackLabel[] = [];
    for (const layer of layers) {
        const s = plan.series.find(p => p.id === layer.id);
        const y = s ? scaleOf(s) : undefined;
        const edge = layer.band[layer.band.length - 1];
        if (!s || !y || !edge) continue;
        const pixels = y(edge.y0) - y(edge.y1);
        if (edge.y1 - edge.y0 < MIN_LABEL_SHARE || pixels < MIN_LABEL_PIXELS) {
            continue;
        }
        stackLabels.push({
            id: s.id,
            name: s.label,
            image: s.image ?? null,
            color: s.color,
            share: readout(s, readoutTime) ?? edge.y1 - edge.y0,
            y: (y(edge.y0) + y(edge.y1)) / 2,
        });
    }

    const axisLabelColor = (axis: PanelAxis): string => {
        if (axis.seriesIds.length !== 1) return axisColor;
        const s = plan.series.find(p => p.id === axis.seriesIds[0]);
        return s && s.axis === 'own' ? s.color : axisColor;
    };

    return (
        <Box
            data-testid={`panel-${plan.id}`}
            sx={{ position: 'relative', width, height }}
        >
            <PanelHeader
                plan={plan}
                readoutTime={readoutTime}
                marginLeft={margins.left}
                marginRight={margins.right}
            />

            <svg
                width={width}
                height={Math.max(0, height - headerHeight)}
                style={{ display: 'block', touchAction: 'pan-y' }}
            >
                <defs>
                    <clipPath id={clipId}>
                        <rect width={innerW} height={innerH} />
                    </clipPath>
                    {layers.map((layer, i) => {
                        const s = plan.series.find(p => p.id === layer.id);
                        if (!s || s.faint) return null;
                        return (
                            <linearGradient
                                key={layer.id}
                                id={`${clipId}-fill-${i}`}
                                x1="0"
                                y1="0"
                                x2="0"
                                y2="1"
                            >
                                <stop
                                    offset="0%"
                                    stopColor={s.color}
                                    stopOpacity={0.5}
                                />
                                <stop
                                    offset="100%"
                                    stopColor={s.color}
                                    stopOpacity={0.22}
                                />
                            </linearGradient>
                        );
                    })}
                </defs>
                <g transform={`translate(${margins.left},${margins.top})`}>
                    {firstScale &&
                        firstScale
                            .ticks(tickCount)
                            .map(tick => (
                                <line
                                    key={`grid-${tick}`}
                                    x1={0}
                                    x2={innerW}
                                    y1={firstScale(tick)}
                                    y2={firstScale(tick)}
                                    stroke={
                                        tick === 0
                                            ? alpha(
                                                  theme.palette.text.primary,
                                                  0.3
                                              )
                                            : gridColor
                                    }
                                />
                            ))}

                    {plan.axes.map(axis => {
                        const y = axisScales.get(axis.key);
                        if (!y) return null;
                        const left = axis.side === 'left';
                        return (
                            <g key={axis.key}>
                                {y.ticks(tickCount).map(tick => (
                                    <text
                                        key={tick}
                                        x={
                                            left
                                                ? -margins.tickGap
                                                : innerW + margins.tickGap
                                        }
                                        y={y(tick)}
                                        dy="0.32em"
                                        textAnchor={left ? 'end' : 'start'}
                                        fontSize={10}
                                        fontFamily="monospace"
                                        fill={axisLabelColor(axis)}
                                    >
                                        {formatTick(axis.unit, tick)}
                                    </text>
                                ))}
                            </g>
                        );
                    })}

                    <g clipPath={`url(#${clipId})`}>
                        {layers.map(layer => {
                            const s = plan.series.find(p => p.id === layer.id);
                            const y = s ? scaleOf(s) : undefined;
                            if (!s || !y || layer.band.length === 0) {
                                return null;
                            }
                            const area = d3
                                .area<StackBand>()
                                .x(p => x(p.t))
                                .y0(p => y(p.y0))
                                .y1(p => y(p.y1))
                                .curve(d3.curveMonotoneX);
                            return (
                                <path
                                    key={`area-${s.id}`}
                                    data-testid={`area-${s.id}`}
                                    d={area(layer.band) ?? undefined}
                                    fill={
                                        s.faint
                                            ? alpha(s.color, 0.07)
                                            : `url(#${clipId}-fill-${layers.indexOf(layer)})`
                                    }
                                />
                            );
                        })}
                        {layers.map(layer => {
                            const s = plan.series.find(p => p.id === layer.id);
                            const y = s ? scaleOf(s) : undefined;
                            if (
                                !s ||
                                !y ||
                                s.faint ||
                                layer.band.length === 0
                            ) {
                                return null;
                            }
                            const top = d3
                                .line<StackBand>()
                                .x(p => x(p.t))
                                .y(p => y(p.y1))
                                .curve(d3.curveMonotoneX);
                            const d = top(layer.band) ?? undefined;
                            return (
                                <g key={`top-${s.id}`}>
                                    <path
                                        d={d}
                                        fill="none"
                                        stroke={theme.palette.background.paper}
                                        strokeWidth={3.5}
                                        strokeLinejoin="round"
                                    />
                                    <path
                                        d={d}
                                        fill="none"
                                        stroke={s.color}
                                        strokeWidth={1.75}
                                        strokeLinejoin="round"
                                    />
                                </g>
                            );
                        })}
                        {plan.series.map(s => {
                            const view = views.get(s.id);
                            if (!view) return null;
                            if (s.draw === 'bars') {
                                const pts = view.points;
                                const max = d3.max(pts, p => p.v) ?? 0;
                                if (max <= 0) return null;
                                const yb = d3
                                    .scaleLinear()
                                    .domain([0, max])
                                    .range([innerH, lineBottom + 4]);
                                const barW = Math.max(
                                    1,
                                    msPerPx > 0
                                        ? stepMs(pts.map(p => p.t)) / msPerPx -
                                              1
                                        : 1
                                );
                                return (
                                    <g key={s.id}>
                                        {pts.map(p => (
                                            <rect
                                                key={p.t}
                                                x={x(p.t)}
                                                y={yb(p.v)}
                                                width={barW}
                                                height={innerH - yb(p.v)}
                                                fill={alpha(s.color, 0.28)}
                                            />
                                        ))}
                                    </g>
                                );
                            }
                            if (s.draw === 'candles') {
                                const y = scaleOf(s);
                                if (!y || view.candles.length === 0) {
                                    return null;
                                }
                                const bodyW = Math.max(
                                    1,
                                    msPerPx > 0
                                        ? (stepMs(view.candles.map(c => c.t)) /
                                              msPerPx) *
                                              0.7
                                        : 1
                                );
                                return (
                                    <g key={s.id}>
                                        {view.candles.map(c => {
                                            const up = c.close >= c.open;
                                            const color = up
                                                ? upColor
                                                : downColor;
                                            const cx = x(c.t);
                                            const top = y(
                                                Math.max(c.open, c.close)
                                            );
                                            const bottom = y(
                                                Math.min(c.open, c.close)
                                            );
                                            return (
                                                <g key={c.t}>
                                                    <line
                                                        x1={cx}
                                                        x2={cx}
                                                        y1={y(c.high)}
                                                        y2={y(c.low)}
                                                        stroke={color}
                                                    />
                                                    <rect
                                                        x={cx - bodyW / 2}
                                                        y={top}
                                                        width={bodyW}
                                                        height={Math.max(
                                                            1,
                                                            bottom - top
                                                        )}
                                                        fill={color}
                                                    />
                                                </g>
                                            );
                                        })}
                                    </g>
                                );
                            }
                            if (s.draw === 'line' || s.draw === 'step') {
                                const y = scaleOf(s);
                                if (!y || view.points.length === 0) return null;
                                const line = d3
                                    .line<SeriesPoint>()
                                    .x(p => x(p.t))
                                    .y(p => y(p.v));
                                if (s.draw === 'step') {
                                    line.curve(d3.curveStepAfter);
                                }
                                const livePoint = liveTailOf(s, view.points);
                                const solid = livePoint
                                    ? view.points.slice(0, -1)
                                    : view.points;
                                return (
                                    <g key={s.id}>
                                        <path
                                            d={line(solid) ?? undefined}
                                            fill="none"
                                            stroke={s.color}
                                            strokeWidth={1.6}
                                            strokeLinejoin="round"
                                            strokeDasharray={dashOf(s, plan.id)}
                                            strokeOpacity={faintOpacityOf(
                                                s,
                                                plan.id
                                            )}
                                        />
                                        {livePoint && (
                                            <path
                                                data-testid={`live-tail-${s.id}`}
                                                d={
                                                    line(
                                                        view.points.slice(-2)
                                                    ) ?? undefined
                                                }
                                                fill="none"
                                                stroke={s.color}
                                                strokeWidth={1.6}
                                                strokeDasharray={LIVE_TAIL_DASH}
                                                pointerEvents="none"
                                            />
                                        )}
                                    </g>
                                );
                            }
                            return null;
                        })}
                    </g>

                    {plan.series.map(s => {
                        const view = views.get(s.id);
                        const y = scaleOf(s);
                        if (!view || !y) return null;
                        const livePoint = liveTailOf(s, view.points);
                        if (
                            !livePoint ||
                            livePoint.t < t0 ||
                            livePoint.t > t1
                        ) {
                            return null;
                        }
                        return (
                            <g
                                key={`live-dot-${s.id}`}
                                data-testid={`live-dot-${s.id}`}
                                pointerEvents="none"
                            >
                                <circle
                                    className="rt-halo"
                                    cx={x(livePoint.t)}
                                    cy={y(livePoint.v)}
                                    r={4}
                                    fill={s.color}
                                    style={LIVE_HALO_STYLE}
                                />
                                <circle
                                    cx={x(livePoint.t)}
                                    cy={y(livePoint.v)}
                                    r={3.5}
                                    fill={s.color}
                                    stroke={theme.palette.background.paper}
                                    strokeWidth={1.5}
                                />
                            </g>
                        );
                    })}

                    {cursorX !== null && (
                        <g pointerEvents="none">
                            <line
                                x1={cursorX}
                                x2={cursorX}
                                y1={0}
                                y2={innerH}
                                stroke={alpha(theme.palette.text.primary, 0.35)}
                                strokeDasharray="3 3"
                            />
                            {cursor !== null &&
                                layers.map(layer => {
                                    const s = plan.series.find(
                                        p => p.id === layer.id
                                    );
                                    const y = s ? scaleOf(s) : undefined;
                                    const at = bandAt(layer.band, cursor);
                                    if (!s || !y || s.faint || !at) return null;
                                    return (
                                        <circle
                                            key={`dot-${s.id}`}
                                            cx={x(at.t)}
                                            cy={y(at.y1)}
                                            r={3.5}
                                            fill={s.color}
                                            stroke={
                                                theme.palette.background.paper
                                            }
                                            strokeWidth={1.5}
                                        />
                                    );
                                })}
                            {cursor !== null &&
                                plan.series.map(s => {
                                    if (s.draw !== 'line' && s.draw !== 'step')
                                        return null;
                                    const y = scaleOf(s);
                                    const value = readout(s, cursor);
                                    if (!y || value === null) return null;
                                    return (
                                        <circle
                                            key={s.id}
                                            cx={cursorX}
                                            cy={y(value)}
                                            r={3.5}
                                            fill={s.color}
                                            stroke={
                                                theme.palette.background.paper
                                            }
                                            strokeWidth={1.5}
                                        />
                                    );
                                })}
                        </g>
                    )}

                    <rect
                        ref={overlayRef}
                        width={innerW}
                        height={innerH}
                        fill="transparent"
                        style={{ cursor: 'crosshair', touchAction: 'pan-y' }}
                        onPointerDown={event => {
                            if (gestures.down(toGesturePointer(event))) {
                                event.currentTarget.setPointerCapture?.(
                                    event.pointerId
                                );
                            }
                        }}
                        onPointerMove={event =>
                            gestures.move(toGesturePointer(event))
                        }
                        onPointerUp={event => {
                            gestures.up(toGesturePointer(event));
                            event.currentTarget.releasePointerCapture?.(
                                event.pointerId
                            );
                        }}
                        onPointerCancel={event =>
                            gestures.cancel(toGesturePointer(event))
                        }
                        onPointerLeave={event =>
                            gestures.leave(toGesturePointer(event))
                        }
                        onDoubleClick={() => gestures.doubleClick()}
                    />

                    {plan.series.map(s => {
                        if (s.draw !== 'markers') return null;
                        const y = scaleOf(s);
                        const markers = views.get(s.id)?.markers ?? [];
                        return (
                            <g key={s.id}>
                                {markers.map(m => {
                                    const mx = x(m.t);
                                    const [lo, hi] = y ? y.domain() : [0, 1];
                                    const my =
                                        y && m.v !== null
                                            ? y(Math.min(hi, Math.max(lo, m.v)))
                                            : layers.length > 0
                                              ? 10
                                              : innerH / 2;
                                    return (
                                        <path
                                            key={m.id}
                                            d={markerPath(
                                                m.shape,
                                                markerSize(m)
                                            )}
                                            transform={`translate(${mx},${my})`}
                                            fill={m.color}
                                            stroke={
                                                theme.palette.background.paper
                                            }
                                            strokeWidth={1.2}
                                            style={{ cursor: 'pointer' }}
                                            onMouseEnter={() =>
                                                setHovered({
                                                    marker: m,
                                                    x: mx,
                                                    y: my,
                                                })
                                            }
                                            onMouseLeave={() =>
                                                setHovered(null)
                                            }
                                        />
                                    );
                                })}
                            </g>
                        );
                    })}

                    {timeLabels.map(label => (
                        <text
                            key={label.t}
                            x={label.x}
                            y={innerH + margins.bottom + 14}
                            textAnchor="middle"
                            fontSize={10}
                            fill={axisColor}
                        >
                            {label.text}
                        </text>
                    ))}
                </g>
            </svg>

            {viewport.panelOverlays[plan.id] && (
                <Box
                    data-testid={`chart-overlay-${plan.id}`}
                    sx={{
                        position: 'absolute',
                        left: margins.left,
                        top: headerHeight + margins.top,
                        width: innerW,
                        height: innerH,
                        pointerEvents: 'none',
                    }}
                >
                    {viewport.panelOverlays[plan.id]}
                </Box>
            )}

            {!compact && (
                <StackLabels
                    labels={stackLabels}
                    right={margins.right + 8}
                    top={headerHeight + margins.top}
                />
            )}

            {!compact &&
                isCursorPanel &&
                cursorX !== null &&
                cursor !== null &&
                !hovered && (
                    <CursorTooltip
                        series={plan.series}
                        panelId={plan.id}
                        time={cursor}
                        x={margins.left + cursorX}
                        top={headerHeight + margins.top + 4}
                        panelWidth={width}
                    />
                )}

            {hovered && (
                <Box
                    sx={{
                        position: 'absolute',
                        left: Math.min(
                            Math.max(8, margins.left + hovered.x + 10),
                            Math.max(8, width - 268)
                        ),
                        top: headerHeight + margins.top + hovered.y + 10,
                        maxWidth: 260,
                        p: 1,
                        borderRadius: 1,
                        border: 1,
                        borderColor: 'divider',
                        bgcolor: 'background.paper',
                        boxShadow: 3,
                        pointerEvents: 'none',
                        zIndex: 2,
                    }}
                >
                    <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>
                        {hovered.marker.title}
                    </Typography>
                    {hovered.marker.detail && (
                        <Typography
                            sx={{ fontSize: '11px', color: 'text.secondary' }}
                        >
                            {hovered.marker.detail}
                        </Typography>
                    )}
                    <Typography
                        sx={{
                            fontSize: '10px',
                            color: 'text.disabled',
                            mt: 0.5,
                        }}
                    >
                        {new Date(hovered.marker.t).toLocaleString()}
                    </Typography>
                </Box>
            )}
        </Box>
    );
}
